import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { forceStaticLayerOn } from 'emergency_data/src/actions/spatialLayers';
import { isExtraInfoOrMeta } from 'emergency_data/src/components/JurisdictionView/helpers';
import { ANI_ALI } from 'emergency_data/src/components/Location/constants/dataSources';
import {
  getDataSource,
  isSourceAniAli,
} from 'emergency_data/src/components/Location/helpers';
import {
  _911INFORM,
  _911_INFORM,
} from 'emergency_data/src/constants/spatialLayers';
import useFetchAgencyLinks from 'emergency_data/src/hooks/useFetchAgencyLinks';
import {
  getMostRecentLoc,
  getMostRecentLocations,
} from 'emergency_data/src/utils/getMostRecentLoc';
import PropTypes from 'prop-types';
import {
  hasNotificationPermission,
  pushNotification,
} from '@rsos/components/capstone/PushNotifications/PushNotifications';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { APPLICATION_AGENT_511 } from '@rsos/constants/locationApplications';
import { APPLE, GOOGLE, SXM } from '@rsos/constants/orgsWithQueueText';
import {
  removeCall,
  setSelectedCallerID,
  inactiveCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setHoveredID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import usePortalType from '@rsos/utils/usePortalType';
import Loader from '../../Loader';
import Box from '../../base/Box';
import EmergencyCall from '../EmergencyCall';
import NoResults from '../NoResults';
import {
  LoaderWrapper,
  NoResultsWrapper,
  ScrollableList,
} from './EmergencyCallsQueue.styles';
import {
  trackClickCall,
  trackClickCallFromQueue,
  trackRemoveCall,
  trackTT911Request,
} from './emergencyCallsQueueTrackings';

const NOTIFICATION_TT911_TITLE = 'New Text-to-911 from RapidSOS';

/**
 *
 * @param {object} emergencyCalls - list of 911 emergency calls
 * @param {boolean} hasAgent511Access - does user have access to view Agent511 text-to-911 requests?
 * @param {String} hoveredCall - ID of call being hovered on the map or in the queue
 * @param {boolean} isInternational - is international support enabled
 * @param {boolean} isVisible - is the current queue visible?
 * or caller ID for use with the Google Maps
 * @param {string} selectedCallerID - the selected caller id
 * @param {Function} onQueueHover - Hover effect handler for markers
 * @param {Function} onRemoveID - function to set state in JV for removed alert
 * @param {Function} onQueueItemClicked- callback when a queue item is clicked
 *
 */
const EmergencyCallsQueue = ({
  emergencyCalls,
  hasAgent511Access,
  hoveredCall,
  isInternational,
  isVisible,
  selectedCallerID,
  irpVersion,
  onQueueHover,
  onRemoveID,
  onQueueItemClicked,
}) => {
  const dispatch = useDispatch();

  const isLoading = emergencyCalls.loading;

  const psaps = useSelector(state => state.psaps);

  const sinatra = useSelector(state => state.sinatra);

  const hasAniAliAccess = checkAniAliAccessSelector({ psaps, sinatra });

  const hasCallStatusAccess = checkCallStatusAccessSelector({ psaps });

  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );

  /**
   * @constant {hoveredCall} - queue/map primary location
   * @constant {hoveredCallerID} - queue/map secondary location (deckGL)
   */
  const hoveredCallerID = useSelector(
    state => state?.hoveredIncident?.hoveredCallerID,
  );

  // If Agent511 is enabled, filter the emergency calls into 2 lists:
  // Text-to-911 requests and 911 calls. Text-to-911 requests will persist on top of the queue,
  // and 911 calls will display below text-to-911 requests.
  const tt911Requests = useMemo(() => {
    return hasAgent511Access
      ? Object.values(emergencyCalls).filter(
          request =>
            request?.isTT911 ||
            request.core.locations[0]?.application === APPLICATION_AGENT_511,
        )
      : [];
  }, [emergencyCalls, hasAgent511Access]);

  const nonTT911Requests = useMemo(() => {
    return hasAgent511Access
      ? Object.values(emergencyCalls).filter(
          call =>
            !call?.isTT911 &&
            call.core.locations[0]?.application !== APPLICATION_AGENT_511,
        )
      : [];
  }, [emergencyCalls, hasAgent511Access]);

  const [emergencyCallID, setEmergencyCallID] = useState(null);
  const [isRemoveCallVisible, setIsRemoveCallVisible] = useState(false);
  const [callsClickedList, setCallsClickedList] = useState([]);
  const [newTT911RequestID, setNewTT911RequestID] = useState('');
  const { portalType } = usePortalType();

  const handleSelected = id => {
    const mostRecentLocation = getMostRecentLoc(emergencyCalls[id]);
    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
    const isConnectedCar = isCallerIDFromOrg(SXM, emergencyCalls, id);

    const isAniAli = isSourceAniAli(mostRecentLocation);
    const serviceType = isAniAli ? ANI_ALI : getDataSource(mostRecentLocation);
    const classOfServices = isAniAli
      ? mostRecentLocation?.class_of_service_category || null
      : null;

    const callStatus = getMostRecentCallStatus({
      callerID: id,
      callQueue: emergencyCalls,
      hasAniAliAccess,
      hasCallStatusAccess,
    });

    if (!callsClickedList.includes(id)) {
      trackClickCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        serviceType,
        classOfServices,
        callStatus,
      );
      setCallsClickedList(callsClickedList.concat(id));
    }

    trackClickCallFromQueue(
      isConnectedCar,
      irpVersion,
      payloadID,
      requestID,
      portalType,
      serviceType,
      classOfServices,
      callStatus,
    );
    dispatch(setSelectedCallerID(id));

    const has911informData = !!emergencyCalls?.[id]?.additionalData?.[
      _911_INFORM
    ];

    if (has911informData) {
      dispatch(forceStaticLayerOn(_911INFORM));
    }
  };

  const handleRightClick = (e, callerID) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedCallerID !== callerID) setEmergencyCallID(callerID);
  };

  const handleRemoveCall = id => {
    const mostRecentLocation = getMostRecentLoc(emergencyCalls[id.caller_id]);
    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);

    const isConnectedCar = isCallerIDFromOrg(SXM, emergencyCalls, id);

    const callStatus = getMostRecentCallStatus({
      callerID: id.caller_id,
      callQueue: emergencyCalls,
      hasAniAliAccess,
      hasCallStatusAccess,
    });

    trackRemoveCall(
      isConnectedCar,
      irpVersion,
      payloadID,
      requestID,
      portalType,
      callStatus,
    );
    dispatch(inactiveCalls(id));
    onRemoveID(id.caller_id);
    dispatch(removeCall(id.caller_id));
    setIsRemoveCallVisible(false);
    dispatch(setHoveredID(null));
  };

  const onClickHandler = () => setEmergencyCallID(null);

  window.oncontextmenu = () =>
    window.removeEventListener('click', onClickHandler, false);

  useEffect(() => {
    window.addEventListener('click', onClickHandler, false);

    return () => {
      window.removeEventListener('click', onClickHandler, false);
    };
  }, []);

  /**
   * @function renderQueueItem reorder the callQueue based on the latest location or secondaryLocation
   * @todo future update. apply getMostRecentLoc to renderQueueItem (cleaner)
   */
  const renderQueueItem = queue =>
    queue
      .sort((a, b) => {
        const {
          mostRecentLocation: location_a,
          mostRecentSecondaryLocation: secondaryLoc_a,
        } = getMostRecentLocations(a);
        const {
          mostRecentLocation: location_b,
          mostRecentSecondaryLocation: secondaryLoc_b,
        } = getMostRecentLocations(b);

        if (location_b && location_a) {
          return location_b.call_start_time - location_a.call_start_time;
        }
        if (secondaryLoc_a && secondaryLoc_b) {
          return (
            secondaryLoc_b.call_start_time - secondaryLoc_a.call_start_time
          );
        }
        if (secondaryLoc_a) {
          return location_b.call_start_time - secondaryLoc_a.call_start_time;
        }
        if (secondaryLoc_b) {
          return secondaryLoc_b.call_start_time - location_a.call_start_time;
        }
      })
      .map((item, index) => {
        const mostRecentLocRecord =
          item.core.locations[item.core.locations?.length - 1] ||
          item.core.secondaryLocations[
            item.core.secondaryLocations?.length - 1
          ];

        const callerID = mostRecentLocRecord.caller_id;
        const isGoogle = isCallerIDFromOrg(GOOGLE, emergencyCalls, callerID);
        const hasGoogleCarCrashData = !!(
          isGoogle &&
          item.additionalData.google &&
          item.additionalData.google.data &&
          item.additionalData.google.data.crash_detection
        );
        const hasGoogleCallerInfo = !!(
          isGoogle &&
          item.additionalData.google &&
          item.additionalData.google.data &&
          item.additionalData.google.data.caller_info
        );
        const isManualSearch = item.isManualSearch;
        const isTT911 = hasAgent511Access && item?.isTT911;
        const isCallerIn911informBuilding = !!item?.additionalData?.[
          '911_inform'
        ];

        const callStatus = getMostRecentCallStatus({
          callerID,
          callQueue: emergencyCalls,
          hasAniAliAccess,
          hasCallStatusAccess,
        });

        return (
          <Box
            callerid={callerID}
            key={`${callerID}-${index}`}
            noShrink={true}
            onContextMenu={e => handleRightClick(e, callerID)}
            onMouseEnter={() =>
              onQueueHover(callerID, 'queue', EMERGENCY_CALLS)
            }
            onMouseLeave={() => onQueueHover(null, 'queue', EMERGENCY_CALLS)}
            onClick={() => onQueueItemClicked(callerID)}
            data-name={mostRecentLocRecord.source}
          >
            <EmergencyCall
              applicationType={mostRecentLocRecord.application}
              classOfService={mostRecentLocRecord?.class_of_service_category}
              callerID={callerID}
              callStartTime={mostRecentLocRecord.call_start_time}
              callStatus={callStatus}
              emergencyCallID={emergencyCallID}
              hasAgent511Access={hasAgent511Access}
              hasGoogleCarCrashData={hasGoogleCarCrashData}
              hasGoogleCallerInfo={hasGoogleCallerInfo}
              isApple={isCallerIDFromOrg(APPLE, emergencyCalls, callerID)}
              isCallerIn911informBuilding={isCallerIn911informBuilding}
              isCallExpired={item.isCallExpired}
              isCallReceived={item?.agencyShareChat?.isCallReceived}
              isCallShared={item?.agencyShareChat?.isCallShared}
              isConnectedCar={isCallerIDFromOrg(SXM, emergencyCalls, callerID)}
              isInternational={isInternational}
              isManualSearch={isManualSearch}
              isMapMarkerHovered={
                callerID === hoveredCall || callerID === hoveredCallerID
              }
              isRemoveCallVisible={isRemoveCallVisible}
              isSelected={callerID === selectedCallerID}
              isTT911={isTT911}
              isUnread={
                item?.showNewCallNotification || item?.agencyShareChat?.isUnread
              }
              isUnseen={item?.agencyShareChat?.isUnseen}
              onRemoveCall={handleRemoveCall}
              setCallerID={handleSelected}
              source={mostRecentLocRecord.source}
              tt911ADR={item?.additionalData?.[APPLICATION_AGENT_511]?.[0]}
            />
          </Box>
        );
      });

  useEffect(() => {
    if (
      tt911Requests &&
      tt911Requests.length &&
      numberWithNewLocation &&
      hasNotificationPermission(irpVersion)
    ) {
      const tt911Request = tt911Requests.find(
        tt911 =>
          tt911.core.locations?.length === 1 &&
          tt911.core.locations[0].caller_id === numberWithNewLocation,
      );
      if (tt911Request)
        setNewTT911RequestID(tt911Request.core.locations[0].caller_id);
    }
  }, [tt911Requests, numberWithNewLocation]);

  useEffect(() => {
    if (newTT911RequestID) {
      pushNotification({
        title: NOTIFICATION_TT911_TITLE,
      });
      setNewTT911RequestID('');
    }
  }, [newTT911RequestID]);

  useEffect(() => {
    if (newTT911RequestID && emergencyCalls[newTT911RequestID]) {
      const callLocations = emergencyCalls[newTT911RequestID].core.locations;
      const firstLocationIndex = callLocations?.length - 1 || 0;
      const { payloadID, requestID } = isExtraInfoOrMeta(
        callLocations[firstLocationIndex],
      );
      trackTT911Request(irpVersion, payloadID, requestID);
    }
  }, [emergencyCalls, irpVersion, newTT911RequestID]);

  // Check agency share/chat access and fetch paired peer agencies.
  useFetchAgencyLinks();

  return (
    isVisible && (
      <>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : Object.keys(emergencyCalls).length ? (
          <ScrollableList>
            {hasAgent511Access ? (
              <>
                <Box data-name="tt911-requests">
                  {renderQueueItem(tt911Requests)}
                </Box>
                <Box data-name="non-tt911-requests">
                  {renderQueueItem(nonTT911Requests)}
                </Box>
              </>
            ) : (
              <Box data-name="emergency-calls">
                {renderQueueItem(Object.values(emergencyCalls))}
              </Box>
            )}
          </ScrollableList>
        ) : (
          <NoResultsWrapper>
            <NoResults
              title="No Emergencies"
              description="No emergency events were found."
            />
          </NoResultsWrapper>
        )}
      </>
    )
  );
};

EmergencyCallsQueue.propTypes = {
  emergencyCalls: PropTypes.object.isRequired,
  hasAgent511Access: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  selectedCallerID: PropTypes.string,
  onQueueHover: PropTypes.func,
  onRemoveID: PropTypes.func,
  onQueueItemClicked: PropTypes.func,
};

EmergencyCallsQueue.defaultProps = {
  hasAgent511Access: false,
  onQueueHover: () => {},
  onRemoveID: () => {},
  onQueueItemClicked: () => {},
};

export default EmergencyCallsQueue;
